import React, { useState } from 'react';
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Box,
  Button,
} from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useSupervisors } from "../hooks/useSupervisors";
import { useAgents } from "../hooks/useAgents";
import { usePagination } from "../hooks/usePagination";
import { useSummaryData } from "../hooks/useSummaryData";
import { useSummaryGeneration } from "../hooks/useSummaryGeneration";
import SummaryConfigurationForm from "../components/insights/SummaryConfigurationForm";
import SummaryTable from "../components/insights/SummaryTable";
import ConfirmationModal from "../components/insights/ConfirmationModal";
import ResultModal from "../components/insights/ResultModal";
import { DEFAULT_PREFERENCES, DEFAULT_SORTING_COLUMN } from "../constants/insightsConstants";

function InsightsCalls() {
  const { t } = useTranslation();
  const history = useHistory();
  
  // Estados locales mínimos
  const [summaryName, setSummaryName] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [filteringText, setFilteringText] = useState("");
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [sortingColumn, setSortingColumn] = useState(DEFAULT_SORTING_COLUMN);
  const [sortingDescending, setSortingDescending] = useState(true);
  
  // Callback para limpiar selecciones cuando se cargan supervisores
  const handleSupervisorsLoaded = React.useCallback((supervisor, agents) => {
    setSelectedSupervisor(supervisor);
    setSelectedAgents(agents);
  }, []);

  // Hooks personalizados
  const supervisors = useSupervisors(dateRange, handleSupervisorsLoaded);
  const agents = useAgents(dateRange, selectedSupervisor, t);
  const pagination = usePagination(dateRange, selectedSupervisor, selectedAgents);
  const summaryData = useSummaryData(
    dateRange,
    selectedSupervisor,
    selectedAgents,
    pagination,
    preferences
  );
  const summaryGeneration = useSummaryGeneration(
    summaryName,
    dateRange,
    selectedItems,
    summaryData.tableItems,
    selectedSupervisor,
    selectedAgents
  );

  // Handler para cambio de supervisor (limpiar agentes cuando cambia)
  const handleSupervisorChange = (newSupervisor) => {
    setSelectedSupervisor(newSupervisor);
    setSelectedAgents([]); // Limpiar agentes cuando cambia supervisor
  };

  // Handler para cambio de preferencias (resetear página si cambia tamaño)
  const handlePreferencesChange = ({ detail }) => {
    setPreferences(detail);
    if (detail.pageSize !== preferences.pageSize) {
      pagination.setCurrentPageIndex(1);
    }
  };

  // Handler para cambio de ordenación
  const handleSortingChange = ({ detail }) => {
    setSortingColumn(detail);
    setSortingDescending(detail.sortingDescending);
  };

  // Handler para cancelar
  const handleCancel = () => {
    history.push('/history-insights-calls');
  };

  // Handler para ver insight (placeholder)
  const handleViewInsight = (item) => {
    // TODO: Implementar navegación o modal para ver insight
    console.log("Ver insight para:", item);
  };
  
  // Calcular pagesCount basándose en si hay más páginas
  const pagesCount = pagination.hasMorePages 
    ? pagination.currentPageIndex + 1 
    : pagination.currentPageIndex;

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("insights.newCXSummaryDescription")}
        >
          {t("insights.newCXSummaryTitle")}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <SummaryConfigurationForm
          summaryName={summaryName}
          dateRange={dateRange}
          selectedSupervisor={selectedSupervisor}
          selectedAgents={selectedAgents}
          supervisors={supervisors.supervisors}
          agents={agents.agents}
          supervisorsEnabled={supervisors.supervisorsEnabled}
          agentsEnabled={agents.agentsEnabled}
          onSummaryNameChange={setSummaryName}
          onDateRangeChange={setDateRange}
          onSupervisorChange={handleSupervisorChange}
          onAgentsChange={setSelectedAgents}
          getAgentsPlaceholder={agents.getAgentsPlaceholder}
          t={t}
        />

        {dateRange && dateRange.startDate && dateRange.endDate && (
          <SummaryTable
            items={summaryData.tableItems}
            loading={summaryData.loading}
            selectedItems={selectedItems}
            onSelectionChange={setSelectedItems}
            filteringText={filteringText}
            onFilteringTextChange={setFilteringText}
            currentPageIndex={pagination.currentPageIndex}
            pagesCount={pagesCount}
            onPageChange={pagination.setCurrentPageIndex}
            preferences={preferences}
            onPreferencesChange={handlePreferencesChange}
            sortingColumn={sortingColumn}
            sortingDescending={sortingDescending}
            onSortingChange={handleSortingChange}
            dateRange={dateRange}
            onViewInsight={handleViewInsight}
            t={t}
          />
        )}

        <Box float="right" padding={{ bottom: "l" }}>
          <SpaceBetween direction="horizontal" size="m">
            <Button
              variant="link"
              onClick={handleCancel}
            >
              {t("insights.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={summaryGeneration.generateSummary}
              disabled={!summaryGeneration.isCreateSummaryButtonEnabled()}
              loading={summaryGeneration.generatingSummary}
            >
              {t("insights.createSummary")}
            </Button>
          </SpaceBetween>
        </Box>
      </SpaceBetween>
      
      <ResultModal
        visible={summaryGeneration.showPostSuccessModal}
        onDismiss={() => summaryGeneration.setShowPostSuccessModal(false)}
        result={summaryGeneration.postRequestResult}
        t={t}
      />
      
      <ConfirmationModal
        visible={summaryGeneration.showConfirmationModal}
        onDismiss={() => summaryGeneration.setShowConfirmationModal(false)}
        onConfirm={summaryGeneration.processAfterConfirmation}
        summaryName={summaryName}
        dateRange={dateRange}
        selectedSupervisor={selectedSupervisor}
        selectedAgents={selectedAgents}
        verificationResult={summaryGeneration.verificationResult}
        generatingSummary={summaryGeneration.generatingSummary}
        t={t}
      />
    </ContentLayout>
  );
}

export default InsightsCalls; 
